<template>
  <div class="search-target-wrapper">
    <!-- header -->
    <div class="header">
      <div class="title-wrapper">
        <w-managed-object-title :title="obj.objName" :sub-title="obj.ipaddress" :icon-class="objIconClass" :show-state="showOperState" :state="operStateValue" :clickable-title="true" @title-click="routeForDetailInfo" />
      </div>
    </div>
    <!-- body -->
    <div class="body">
      <el-scrollbar v-loading="isLoading">
        <w-managed-object-basic-info-view ref="basicInfo" :object-info="obj" />
        <w-e2e-agent-combobox  v-if="showAgentSelectBox" :agent-list="agentList" @change="onChangeAgent" />
        <w-managed-object-perf-info-view v-if="showPerfInfo" ref="perfInfo" />
        <w-common-event-info-view ref="eventInfo" @click-detail="routeForDetailInfo" />
      </el-scrollbar>
    </div>
  </div>
</template>

<script>
import { isEmpty } from 'lodash';
import { getE2eAgentList, loadObjMajorInfo, getObjTypeIconClass, isVisibleOperStateOfObj, isVisiblePerfInfo, isVisibleAgentCombobox, routeForDetailInfo, getObjStatusToBoolean } from '@/utils/obj-major-info';
import WManagedObjectTitle from '@/views/topologymap/components/major-info/ManagedObjectTitle';
import WManagedObjectBasicInfoView from '@/views/topologymap/components/major-info/ManagedObjectBasicInfoView';
import WManagedObjectPerfInfoView from '@/views/topologymap/components/major-info/ManagedObjectPerfInfoView';
import WE2eAgentCombobox from '@/views/topologymap/components/major-info/components/E2eAgentCombobox';
import WCommonEventInfoView from '@/views/topologymap/components/major-info/CommonEventInfoView';
import MapConst from '@/constants/topologymap';

export default {
  name: 'WSearchTargetObjInfo',
  components: {
    WManagedObjectTitle,
    WManagedObjectBasicInfoView,
    WManagedObjectPerfInfoView,
    WCommonEventInfoView,
    WE2eAgentCombobox,
  },
  props: {
    data: {
      type: Object,
    },
  },
  watch: {
    data: {
      immediate: true,
      handler() {
        this.reset();
        if (!isEmpty(this.data)) {
          this.loadData();
        }
      },
    },
  },
  data() {
    return {
      obj: {},
      isLoading: false,
      /**
       * 운영상태 값
       * @type {Object}
       */
      operStateValue: null,
      /**
       * E2E에 대해 에이전트 정보 선택 콤보박스 값
       * @type {[type]}
       */
      selectedAgentObjId: null,
      /**
       * E2E 대상에 연결된 에이전트 리스트
       * @type {Array}
       */
      agentList: [],
      /**
       * E2E에 대해 선택 된 에이전트 정보
       * @type {Object}
       */
      agentCommonInfo: {},
    };
  },
  computed: {
    /**
     * 성능정보 표현 여부 (서비스는 성능정보 미제공)
     */
    showPerfInfo() {
      return isVisiblePerfInfo(this.obj.objDefineId);
    },
    /**
     * agent 선택 콤보박스 표현 여부(E2E일 경우 agent 선택할 수 있는 콤보박스 제공)
     */
    showAgentSelectBox() {
      return isVisibleAgentCombobox(this.obj.objDefineId);
    },
    /**
     * 운영상태 표현 여부
     */
    showOperState() {
      return isVisibleOperStateOfObj(this.obj.objDefineId);
    },
    /**
     * 장비 아이콘 클래스
     * @author Ju
     * @return {String}
     */
    objIconClass() {
      return isEmpty(this.obj) ? null : getObjTypeIconClass(this.obj.objManagetypeId, this.obj.objDefineId, this.obj.objtypeId);
    },
  },
  methods: {
    loadData() {
      this.$nextTick(() => {
        this.obj = this.data;
        if (this.obj.objDefineId === 'E2E_SERVICE') {
          getE2eAgentList(this.obj.objId).then((response) => {
            this.agentCommonInfo = response.data.data.agentCommonInfo;
            this.agentList = response.data.data.agentList;
          });
        } else {
          this.loadMajorInfo();
        }
      });
    },
    loadMajorInfo() {
      const objDefineId = this.obj.objDefineId;
      const agentInfo = objDefineId === 'E2E_SERVICE' ? { ...this.agentCommonInfo, objId: this.selectedAgentObjId } : null;
      this.isLoading = true;
      loadObjMajorInfo(this.obj, agentInfo, true)
        .then((result) => {
          if (!isEmpty(result.majorInfoData)) {
            const majorInfoData = result.majorInfoData;
            this.$refs.basicInfo.setData(majorInfoData);
            if (this.showPerfInfo) {
              this.$refs.perfInfo.setData(majorInfoData.perfInfo, objDefineId, this.obj.objtypeId, result.rscNameList);
            }
            this.$refs.eventInfo.setData(majorInfoData.eventInfo, MapConst.EVENT_TYPE.STATUS, objDefineId);
            this.operStateValue = isEmpty(majorInfoData.stateValue) ? null : getObjStatusToBoolean(majorInfoData.stateValue[0].value, this.obj.objDefineId, this.objtypeId);
          }
          const predictList = isEmpty(result.predictEventHist) ? [] : result.predictEventHist;
          this.$refs.eventInfo.setData(predictList, MapConst.EVENT_TYPE.PREDICT, objDefineId);
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    /**
     * 하위 컴포넌트 데이터 초기화
     * @type {Boolean}
     */
    reset() {
      Object.keys(this.$refs).forEach((key) => {
        if (!isEmpty(this.$refs[key])) {
          this.$refs[key].initData();
        }
      });
      this.operStateValue = null;
      this.selectedAgentObjId = null;
      this.agentList = [];
      this.agentCommonInfo = {};
    },
    onChangeAgent(objId) {
      this.selectedAgentObjId = objId;
      this.loadObjMajorInfo();
    },
    /**
     * 상세 정보 화면으로 이동한다.
     * @author Ju
     * @param  {[type]} infoType [description]
     * @return {[type]}          [description]
     */
    routeForDetailInfo(infoType) {
      routeForDetailInfo(this, this.obj, infoType);
    },
  },
};
</script>

<style></style>



// WEBPACK FOOTER //
// src/views/frame/components/popup/SmartSearchPopup/SearchResultView/SearchTargetObjInfo.vue